import React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import Avatar from "../../Avatar";
import Button from "../../Button";
import Flex from "../../Flex";
import Icon from "../../Icon";
import Popover from "../../Popover";
import { H6 } from "../../Typography";
import TeamTypeBar, { TeamTypeBarPlaceholder } from "../components/TeamTypeBar";
import { PinnedItemShape } from "../types/sideNav";
import {
  SectionItem,
  SectionItemList,
  SectionItemName,
  SectionPinnedLink,
  SectionPinnedLinkContent,
  SectionResponsiveContainer,
  CollapsedAvatarWrapper,
  CollapsedItemLink,
  CollapsedItemWrapper,
  UnpinButtonWrapper,
} from "../styles/sideNavSection.styles";

/**
 * SideNavPinnedSection - "Pinned" section of the side navigation.
 *
 * Renders pinned teams, people, and tags using the canonical SideNav row
 * layout. Consumers no longer need a custom `renderPinnedSection`; just
 * enrich each item with the optional fields below.
 *
 * Requires a `react-router-dom` `<Router>` ancestor: each pinned row uses
 * `<Link>` for navigation and the active-state highlight is derived from
 * `useLocation()`. The same applies to `SideNavTeamsSection`. (This is
 * already the case for the rest of `SideNav`, e.g. `NavItem` and
 * `SideNavCurrentViewSection`.)
 *
 * @param {Array} items - Pinned items
 * @param {string} items[].name - Display name
 * @param {string} items[].link - Route path
 * @param {string} [items[].avatar] - Avatar image URL
 * @param {string} [items[].shape] - Avatar shape
 * @param {string} [items[].gradient] - Avatar gradient fallback
 * @param {string} [items[].entityType] - "team" | "person" | "tag"
 * @param {string} [items[].type] - Team type label (only used when entityType is "team")
 * @param {string} [items[].badgeVariant] - Token mapping for the team-type bar
 * @param {React.ReactNode} [items[].popoverBadge] - Badge node rendered inside the row tooltip
 * @param {(item) => void} [items[].onUnpin] - When set, renders the unpin button
 * @param {string|number} [items[].id] - Stable React key
 * @param {boolean} isExpanded - Whether the side nav is in its expanded state
 */
const SideNavPinnedSection = ({ items, isExpanded }) => {
  const location = useLocation();

  if (!items || items.length === 0) {
    return null;
  }

  const renderPinnedItem = (item) => {
    const {
      avatar,
      name,
      link,
      shape,
      onUnpin,
      gradient,
      type,
      entityType,
      badgeVariant = "default",
      popoverBadge,
      id,
    } = item;

    if (!name || !link) {
      return null;
    }

    const showTeamType = entityType === "team" && Boolean(type);
    const isActive = link && location.pathname.startsWith(link);

    const popoverContent = popoverBadge ? (
      <Flex gap="s" alignItems="center">
        <span>{name}</span>
        {popoverBadge}
      </Flex>
    ) : (
      <span>{name}</span>
    );

    if (isExpanded) {
      return (
        <SectionItem
          key={id || name}
          isExpanded={isExpanded}
          className={isActive ? "active" : ""}
        >
          <SectionPinnedLink
            to={link}
            aria-label={name}
            data-pinned-link="true"
          >
            <Popover
              text={popoverContent}
              direction="right"
              width="fit-content"
              tabIndex="-1"
              inlineBlock={false}
              style={{ width: "100%", display: "flex", flex: 1 }}
            >
              <SectionPinnedLinkContent>
                <CollapsedAvatarWrapper>
                  <Avatar
                    image={avatar}
                    customSize="24px"
                    shape={shape}
                    gradient={gradient}
                  />
                </CollapsedAvatarWrapper>
                {showTeamType ? (
                  <TeamTypeBar badgeVariant={badgeVariant} />
                ) : (
                  <TeamTypeBarPlaceholder />
                )}
                <SectionItemName>{name}</SectionItemName>
              </SectionPinnedLinkContent>
            </Popover>
          </SectionPinnedLink>
          {onUnpin && (
            <UnpinButtonWrapper>
              <Popover
                text="Unpin item"
                direction="right"
                width="fit-content"
                tabIndex="-1"
              >
                <Button
                  height="20px"
                  width="20px"
                  borderRadius="20px"
                  variant="borderGrey"
                  borderColor="white"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onUnpin(item);
                  }}
                  type="button"
                >
                  <Icon
                    icon={["fas", "thumbtack"]}
                    color="bodyText"
                    size="sm"
                  />
                </Button>
              </Popover>
            </UnpinButtonWrapper>
          )}
        </SectionItem>
      );
    }

    return (
      <SectionItem key={id || name} isExpanded={isExpanded}>
        <Popover
          text={popoverContent}
          direction="right"
          width="fit-content"
          tabIndex="-1"
        >
          <CollapsedItemWrapper className={isActive ? "active" : ""}>
            <CollapsedItemLink to={link} aria-label={name}>
              <CollapsedAvatarWrapper>
                <Avatar
                  image={avatar}
                  customSize="24px"
                  shape={shape}
                  gradient={gradient}
                />
              </CollapsedAvatarWrapper>
              {showTeamType ? (
                <TeamTypeBar badgeVariant={badgeVariant} />
              ) : (
                <TeamTypeBarPlaceholder />
              )}
            </CollapsedItemLink>
          </CollapsedItemWrapper>
        </Popover>
      </SectionItem>
    );
  };

  return (
    <SectionResponsiveContainer
      flexDirection="column"
      gap="r"
      my={isExpanded ? "s" : "0"}
      mb="0"
      pb="0"
    >
      {isExpanded && (
        <H6 sizing="small" color="greyDark">
          Pinned
        </H6>
      )}
      <SectionItemList isExpanded={isExpanded}>
        {items.map(renderPinnedItem)}
      </SectionItemList>
    </SectionResponsiveContainer>
  );
};

SideNavPinnedSection.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(PinnedItemShape)),
  isExpanded: PropTypes.bool,
};

export default SideNavPinnedSection;
